import { DatabaseData, DatabaseType } from '../types/database'
import { detectDatabaseType } from '../utils/file'
import { SQLiteService } from './sqliteService'
import { CSVService } from './csvService'
import { JSONService } from './jsonService'
import { AccessService } from './accessService'
import { PostgreSQLService } from './postgresqlService'

export class DatabaseService {
  private static instance: DatabaseService
  private sqliteService: SQLiteService
  private csvService: CSVService
  private jsonService: JSONService
  private accessService: AccessService
  private postgresqlService: PostgreSQLService

  private constructor() {
    this.sqliteService = SQLiteService.getInstance()
    this.csvService = CSVService
    this.jsonService = JSONService
    this.accessService = AccessService.getInstance()
    this.postgresqlService = PostgreSQLService.getInstance()
  }

  static getInstance(): DatabaseService {
    if (!DatabaseService.instance) {
      DatabaseService.instance = new DatabaseService()
    }
    return DatabaseService.instance
  }

  async parseDatabase(file: File): Promise<DatabaseData> {
    const fileType = detectDatabaseType(file.name)
    
    switch (fileType) {
      case 'sqlite':
        const buffer = await file.arrayBuffer()
        return await this.sqliteService.parseDatabase(buffer)
      case 'csv':
        return await CSVService.parseCSV(file)
      case 'tsv':
        return await CSVService.parseCSV(file, '\t')
      case 'json':
        return await JSONService.parseJSON(file)
      case 'access':
        return await this.accessService.parseDatabase(file)
      case 'postgresql':
        return await this.postgresqlService.parseDatabase(file)
      default:
        throw new Error(`サポートされていないファイル形式: ${fileType}`)
    }
  }

  async exportDatabase(data: DatabaseData, originalFileName: string): Promise<Blob> {
    const fileType = detectDatabaseType(originalFileName)
    
    switch (fileType) {
      case 'sqlite':
        return await this.sqliteService.exportDatabase(data, originalFileName)
      case 'csv':
        return CSVService.exportCSV(data, originalFileName)
      case 'tsv':
        return CSVService.exportCSV(data, originalFileName)
      case 'json':
        return JSONService.exportJSON(data, originalFileName)
      case 'access':
        return await this.accessService.exportDatabase(data, originalFileName)
      case 'postgresql':
        return await this.postgresqlService.exportDatabase(data, originalFileName)
      default:
        throw new Error(`サポートされていないファイル形式: ${fileType}`)
    }
  }

  getSupportedFormats() {
    return [
      {
        type: 'sqlite',
        extensions: ['.db', '.sqlite', '.sqlite3'],
        description: 'SQLite Database'
      },
      {
        type: 'csv',
        extensions: ['.csv'],
        description: 'CSV File'
      },
      {
        type: 'tsv',
        extensions: ['.tsv'],
        description: 'TSV File'
      },
      {
        type: 'json',
        extensions: ['.json'],
        description: 'JSON File'
      },
      {
        type: 'access',
        extensions: ['.mdb', '.accdb'],
        description: 'Microsoft Access Database'
      },
      {
        type: 'postgresql',
        extensions: ['.sql', '.dump', '.backup'],
        description: 'PostgreSQL Database'
      }
    ]
  }
} 